// src/components/common/PageHeader.js
import React from 'react';
import { Box, Typography, IconButton, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { Refresh } from '@mui/icons-material';

// رموز نظام التصميم الموحّد
const DS = {
  ink: '#0f172a',
  muted: '#64748b',
  border: '#e9eef5',
  brand: '#0ea5e9',
  brandDark: '#0284c7',
  brandSoft: '#e6f6fe',
  shadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
};

// ملاحظة: خاصية variant القديمة لم تعد مستخدمة — كل الرؤوس بنفس الشكل الموحّد
// (الصفحات التي ما زالت تمرّرها لن تتأثر؛ يتم تجاهلها).
const PageHeader = ({
  title,
  subtitle,
  onRefresh,
  refreshing = false,
  actions = null,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        mb: { xs: 2, sm: 3, md: 4 },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: { xs: 2, sm: 2 },
        flexWrap: 'wrap',
        borderRadius: '20px',
        p: { xs: 2, sm: 2.5 },
        bgcolor: '#ffffff',
        border: `1px solid ${DS.border}`,
        boxShadow: DS.shadow,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          insetInlineStart: 0,
          top: 0,
          bottom: 0,
          width: 4,
          background: `linear-gradient(180deg, ${DS.brand}, ${DS.brandDark})`,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' }, flexGrow: 1 }}>
        <Box
          sx={{
            width: { xs: 52, sm: 58 },
            height: { xs: 52, sm: 58 },
            borderRadius: 3,
            display: 'grid',
            placeItems: 'center',
            bgcolor: DS.brandSoft,
            flexShrink: 0,
          }}
        >
          <img
            src="/meta-cloud-logo.png"
            alt="Meta Cloud"
            style={{ height: isMobile ? 32 : 36, width: 'auto', objectFit: 'contain' }}
          />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '1.2rem', sm: '1.45rem' },
              lineHeight: 1.25,
              fontWeight: 800,
              color: DS.ink,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.88rem' }, color: DS.muted, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-end' },
          alignItems: 'center',
        }}
      >
        {onRefresh && (
          <Tooltip title="تحديث البيانات">
            <IconButton
              onClick={onRefresh}
              disabled={refreshing}
              sx={{
                bgcolor: DS.brandSoft,
                color: DS.brandDark,
                transition: 'all 0.3s ease',
                p: { xs: 1, sm: 1.25 },
                '&:hover': { bgcolor: '#d3effd' },
              }}
            >
              <Refresh
                sx={{
                  transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                  transition: 'transform 1s linear',
                  fontSize: { xs: '1.2rem', sm: '1.35rem' },
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        {actions}
      </Box>
    </Box>
  );
};

export default PageHeader;
